import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { CREDIT_LIMIT_STATUS, CREDIT_LIMIT_TYPES, MODAL_TYPES, strings } from '@constants'
import { Spacings, Text } from '@styles'
import { getOrganisationKycData, uploadKYCDocument, createCreditLimit, uploadSupportedDoc } from '@data'
import { updateCreditLimit } from '@data/conn/put/creditLimit'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer, toggleFileViewer, toggleModal } from '@data/state/action/root'
import { useForceUpdate } from '@hooks'
import { RadioButton, TOAST_TYPE, UploadButton, DrawerBodyWrapper, DrawerFooterWrapper } from '@base'
import { Button, Calendar, Input, Select, Textarea } from '@baseV2'

import { InputsWrapper } from '../styles'

const Row = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${Spacings.SPACING_3B};
`

const SectionTitle = styled.div`
	font-size: ${Text.SMALL};
	font-weight: 600;
	color: ${({ theme }) => theme.colors.text.primary};
	margin-top: ${Spacings.SPACING_2B};
`

const RadioSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
`

const RadioGroup = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${Spacings.SPACING_4B};
`

const RadioOption = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
`

const RadioLabel = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
`

const DocRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_3B};
`

const DocTitle = styled.span`
	flex: 1;
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
`

const RequiredMark = styled.span`
	color: ${({ theme }) => theme.colors.status.error.text};
`

const ButtonRow = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_2B};
`

const FooterActions = styled.div`
	display: flex;
	width: 100%;
	gap: ${Spacings.SPACING_3B};
`

const statusTypes = Object.entries(CREDIT_LIMIT_STATUS).map(([key, value]) => ({ key, label: value }))

const deriveFormState = (data) => {
	const _state = {
		type: { value: CREDIT_LIMIT_TYPES.ADHOC },
		sanction_date: { id: 'sanction-date', label: strings('sanction_date'), value: new Date().getTime(), disabled: !!data, required: false },
		expiry_date: { id: 'expiry_date', label: strings('expiry_date'), value: new Date().getTime(), disabled: !!data, required: false },
		status: {
			id: 'status',
			label: strings('status'),
			placeholder: strings('select', 'status'),
			value: statusTypes.find((item) => item.label === CREDIT_LIMIT_STATUS.ACTIVE),
			data: statusTypes,
			primaryKey: 'key',
			displayKey: 'label',
			disabled: !!data,
			required: false,
		},
		commodity: {
			id: 'commodity',
			label: strings('commodity'),
			placeholder: strings('select', 'commodity'),
			value: '',
			primaryKey: 'commodity_id',
			displayKey: 'commodity_name',
			required: false,
			disabled: false,
		},
		amount: { id: 'amount', label: strings('amount'), placeholder: strings('enter', 'amount'), value: '', disabled: !!data, required: true },
		aadhaar: { id: 'aadhaar', value: '', disabled: !!data, required: false },
		pan_doc: { id: 'pan-doc', value: '', disabled: !!data, required: false },
		bank_statement: { id: 'bank-statement', value: '', disabled: !!data, required: false },
		financial_document: { id: 'financial-document', value: '', disabled: !!data, required: false },
		supported_documents: { id: 'supported-document', value: [], disabled: !!data, required: false },
		gst_certificate: { id: 'gst-cerificate', value: '', disabled: !!data, required: false },
	}

	if (data) {
		_state.type.value = data?.type
		_state.status.value = statusTypes.find((item) => item.key === data?.status)
		_state.amount.value = data?.amount
		_state.sanction_date.value = data?.sanction_date
		_state.expiry_date.value = data?.expiry_date
		_state.supported_documents.value = data?.supported_documents
		_state.commodity.value = data?.commodity
	}
	return _state
}

const AddCreditLimit = ({ data, orgId, userData, outletUpdate, isEdit = false, commodities = [] }) => {
	const dispatch = useDispatch()
	const [forceUpdate, update] = useForceUpdate()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [deleteMessage, setDeleteMessage] = useState('')
	const [formState, setFormState] = useState(deriveFormState(data))
	const [curDocType, setCurDocType] = useState('')

	const setKycData = (kyc) => {
		setFormState((_state) => ({
			..._state,
			pan_doc: { ..._state.pan_doc, value: kyc?.kyc_documents?.pan ?? '' },
			aadhaar: { ..._state.aadhaar, value: kyc?.kyc_documents?.aadhaar ?? '' },
			bank_statement: { ..._state.bank_statement, value: kyc?.kyc_documents?.bank_statement ?? '' },
			gst_certificate: { ..._state.gst_certificate, value: kyc?.kyc_documents?.gst_certificate ?? '' },
			financial_document: { ..._state.financial_document, value: kyc?.kyc_documents?.financial_document ?? '' },
		}))
	}

	useEffect(() => {
		setFormState(deriveFormState(data))
	}, [forceUpdate, data])

	useEffect(() => {
		getOrganisationKycData(orgId)
			.then((response) => setKycData(response))
			.catch((error) => console.log(error))
	}, [forceUpdate, orgId])

	const updateState = (key) => (value) => setFormState((_s) => ({ ..._s, [key]: { ..._s[key], value } }))

	const downloadDocument = (filePath) => dispatch(toggleFileViewer(true, { files: filePath }))

	const uploadFileHandler = (key) => (file) => {
		dispatch(showLoader(true))
		uploadKYCDocument(file, key, orgId, null)
			.then((res) => {
				dispatch(showToast(true, strings('upload_success'), { type: TOAST_TYPE.SUCCESS }))
				setKycData(res)
			})
			.catch((err) => {
				dispatch(showToast(true, strings('document_upload_failed'), { type: TOAST_TYPE.ERROR }))
				console.log(err)
			})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const submitHandler = () => {
		createCreditLimit({
			amount: formState?.amount?.value,
			created_at: new Date().getTime(),
			created_by: loggedInUser.getUserDetails()?.user_id,
			expiry_date: formState?.expiry_date?.value,
			sanction_date: formState?.sanction_date?.value,
			status: formState?.status?.value?.key,
			type: formState?.type?.value,
			user_details: userData,
			supported_documents: formState?.supported_documents?.value?.length > 0 ? formState?.supported_documents?.value : null,
			commodity: formState?.commodity?.value !== '' ? formState?.commodity?.value : null,
		})
			.then(() => dispatch(showToast(true, strings('msg_credit_limit_added_success'), { type: TOAST_TYPE.SUCCESS })))
			.catch((error) => {
				console.log(error)
				dispatch(showToast(true, strings('msg_credit_limit_added_fail'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				outletUpdate()
				dispatch(toggleDrawer(false))
			})
	}

	const supportDocUploader = (file) => {
		dispatch(showLoader(true))
		uploadSupportedDoc(file)
			.then((response) => {
				const _supDoc = formState.supported_documents?.value ?? []
				_supDoc.push({ type: curDocType, path: response })
				updateState('supported_documents')(_supDoc)
			})
			.catch((error) => console.log(error))
			.finally(() => {
				setCurDocType('')
				dispatch(showLoader(false))
			})
	}

	const deleteSupportDoc = (index) => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: strings('msg_are_you_sure_want_to_delete_doc'),
				confirmAction: (actionType) => {
					dispatch(showLoader(true))
					if (actionType) {
						const _supDocList = formState.supported_documents.value?.filter((_, _index) => _index !== index)
						updateState('supported_documents')(_supDocList)
					}
					dispatch(showLoader(false))
					dispatch(toggleModal(false))
				},
			})
		)
	}

	const deleteHandler = () => {
		dispatch(showLoader(true))
		updateCreditLimit({ ...data, status: 'DELETE', deleted_on: new Date().getTime() })
			.then(() => dispatch(showToast(true, strings('msg_credit_limit_deleted_success'), { type: TOAST_TYPE.SUCCESS })))
			.catch((error) => {
				let error_msg = strings('msg_credit_limit_deleted_fail')
				if (error.client_error_code === 'E_002' && error?.message) error_msg = error?.message
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				outletUpdate()
				dispatch(toggleDrawer(false))
				dispatch(showLoader(false))
			})
	}

	const disabled = useMemo(
		() => Object.values(formState).some((d) => d.required && !d.value) || curDocType !== '',
		[formState, curDocType]
	)

	const SingleDocumentUpload = ({ type, file = null, mandatory = false, title, disabled: docDisabled = false }) => (
		<DocRow>
			<DocTitle>
				{title} {mandatory && <RequiredMark>{strings(['(', 'required', ')'])}</RequiredMark>}
			</DocTitle>
			<ButtonRow>
				<Button variant='outline' color='neutral' size='small' disabled={!file} onClick={() => downloadDocument(file)}>
					{strings('view')}
				</Button>
				<UploadButton xs variant='destructive' disabled={docDisabled} label={!file ? strings('upload') : strings('change')} onChange={uploadFileHandler(type)} />
			</ButtonRow>
		</DocRow>
	)

	const TYPE_OPTIONS = [
		{ key: CREDIT_LIMIT_TYPES.ADHOC, label: strings('ADHOC') },
		{ key: CREDIT_LIMIT_TYPES.ENHANCEMENT, label: strings('enhancement') },
		{ key: CREDIT_LIMIT_TYPES.BASE, label: strings('base') },
		{ key: CREDIT_LIMIT_TYPES.EXPIRED, label: strings('expired') },
	]

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<Row>
						<Calendar
							id={formState.sanction_date.id}
							label={formState.sanction_date.label}
							value={formState.sanction_date.value}
							onChange={updateState('sanction_date')}
							disabled={formState.sanction_date.disabled}
						/>
						<Calendar
							id={formState.expiry_date.id}
							label={formState.expiry_date.label}
							value={formState.expiry_date.value}
							onChange={updateState('expiry_date')}
							disabled={formState.expiry_date.disabled}
						/>
					</Row>
					<Select
						id={formState.status.id}
						label={formState.status.label}
						placeholder={formState.status.placeholder}
						value={formState.status.value}
						data={formState.status.data}
						primaryKey={formState.status.primaryKey}
						displayKey={formState.status.displayKey}
						disabled={formState.status.disabled}
						onChange={updateState('status')}
					/>
					<Input
						id={formState.amount.id}
						type='number'
						label={formState.amount.label}
						placeholder={formState.amount.placeholder}
						value={formState.amount.value}
						disabled={formState.amount.disabled}
						onChange={(e) => updateState('amount')(e.target.value)}
					/>
					<Select
						id={formState.commodity.id}
						label={formState.commodity.label}
						placeholder={formState.commodity.placeholder}
						value={formState.commodity.value ?? {}}
						data={commodities}
						primaryKey={formState.commodity.primaryKey}
						displayKey={formState.commodity.displayKey}
						disabled={formState.commodity.disabled}
						onChange={updateState('commodity')}
					/>

					<RadioSection>
						<SectionTitle>{strings('type', ':')}</SectionTitle>
						<RadioGroup>
							{TYPE_OPTIONS.map((opt) => (
								<RadioOption key={opt.key}>
									<RadioButton
										id={`credit-type-${opt.key}`}
										size='1.125'
										checked={formState?.type?.value === opt.key}
										value={opt.key}
										onChange={(e) => updateState('type')(e.target.value)}
										disabled={isEdit}
									/>
									<RadioLabel>{opt.label}</RadioLabel>
								</RadioOption>
							))}
						</RadioGroup>
					</RadioSection>

					<SectionTitle>{strings('msg_upload_document', 'director')}</SectionTitle>
					<SingleDocumentUpload type='pan' title={strings('pan')} file={formState?.pan_doc?.value} disabled={formState?.pan_doc?.disabled} />
					<SingleDocumentUpload type='aadhaar' title={strings('aadhaar')} file={formState?.aadhaar?.value} disabled={formState?.aadhaar?.disabled} />
					<SingleDocumentUpload type='gst_certificate' title={strings('gst')} file={formState?.gst_certificate?.value} disabled={formState?.gst_certificate?.disabled} />
					<SingleDocumentUpload type='bank_statement' title={strings('bank', 'statement')} file={formState?.bank_statement?.value} disabled={formState?.bank_statement?.disabled} />
					<SingleDocumentUpload type='financial_document' title={strings('financial', 'document')} file={formState?.financial_document?.value} disabled={formState?.financial_document?.disabled} />

					{(!isEdit || formState?.supported_documents?.value?.length > 0) && <SectionTitle>{strings('supported', 'documents')}</SectionTitle>}
					{formState.supported_documents?.value?.map((item, index) => (
						<DocRow key={`sup-doc-${index}`}>
							<DocTitle>{item.type}</DocTitle>
							<ButtonRow>
								<Button variant='outline' color='neutral' size='small' onClick={() => downloadDocument(item?.path)}>
									{strings('view')}
								</Button>
								<Button variant='outline' color='danger' size='small' onClick={() => deleteSupportDoc(index)} disabled={isEdit}>
									{strings('delete')}
								</Button>
							</ButtonRow>
						</DocRow>
					))}
					{!isEdit && (
						<DocRow>
							<Input
								id='supported-documents'
								value={curDocType}
								label={strings('document', 'type')}
								placeholder={strings(['enter', 'document', 'type'])}
								onChange={(e) => setCurDocType(e.target.value)}
							/>
							<UploadButton variant='destructive' xs label={strings('upload')} disabled={!curDocType} onChange={supportDocUploader} />
						</DocRow>
					)}

					{formState.type.value === 'ADHOC' && isEdit && (
						<Textarea
							id='delete-message'
							label={strings(['remark', 'to', 'delete'])}
							rows={3}
							placeholder={strings('msg_share_any_more_details')}
							value={deleteMessage}
							onChange={(e) => setDeleteMessage(e.target.value)}
						/>
					)}
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<FooterActions>
					<Button color='primary' onClick={submitHandler} disabled={disabled || isEdit}>
						{strings('add')}
					</Button>
					{data && formState.type.value === 'ADHOC' && (
						<Button variant='outline' color='danger' onClick={deleteHandler} disabled={!deleteMessage}>
							{strings('delete')}
						</Button>
					)}
				</FooterActions>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddCreditLimit
